(() => {
  const selector = '[data-home-product-matcher]';
  const initialized = new WeakSet();

  const initialize = (section) => {
    if (!section || initialized.has(section)) return;

    const controls = section.querySelector('[data-matcher-controls]');
    const capacityQuestion = section.querySelector('[data-matcher-question="capacity"]');
    const modelQuestion = section.querySelector('[data-matcher-question="floor-model"]');
    const stage = section.querySelector('[data-matcher-stage]');
    const result = section.querySelector('[data-matcher-result]');
    const templates = new Map(
      [...section.querySelectorAll('template[data-product-key]')].map((template) => [template.dataset.productKey, template]),
    );
    const state = { coverage: 'floor', capacity: 'extended', floorModel: 'x1' };
    const imagePreloads = new Map();
    let displayedImageSignature = '';
    let stageRequest = 0;

    const imageAttributes = (image) => ({
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      sizes: image.getAttribute('sizes') || '',
    });

    const imageSignature = (image) => {
      const { src, srcset, sizes } = imageAttributes(image);
      return [src, srcset, sizes].join('|');
    };

    const loadImage = (sourceImage, priority = 'low') => {
      const signature = imageSignature(sourceImage);
      const existing = imagePreloads.get(signature);

      if (existing) {
        if (priority === 'high') existing.image.fetchPriority = 'high';
        return existing.promise;
      }

      const image = new Image();
      const { src, srcset, sizes } = imageAttributes(sourceImage);
      image.decoding = 'async';
      image.fetchPriority = priority;
      if (sizes) image.sizes = sizes;
      if (srcset) image.srcset = srcset;

      const promise = new Promise((resolve) => {
        image.addEventListener(
          'load',
          async () => {
            if (typeof image.decode === 'function') {
              try {
                await image.decode();
              } catch {
                // A completed load is still safe to display when decode is unavailable.
              }
            }
            resolve(true);
          },
          { once: true },
        );
        image.addEventListener('error', () => resolve(false), { once: true });
      });

      imagePreloads.set(signature, { image, promise });
      image.src = src;
      return promise;
    };

    const templateImage = (key) => templates.get(key)?.content.querySelector('img');

    const preloadKey = (key, priority = 'low') => {
      const image = templateImage(key);
      if (image) loadImage(image, priority);
    };

    const canPreloadAhead = () => {
      const connection = navigator.connection;
      if (!connection) return true;
      return !connection.saveData && !['slow-2g', '2g'].includes(connection.effectiveType);
    };

    const preloadAlternatives = () => {
      if (!canPreloadAhead()) return;
      for (const key of templates.keys()) preloadKey(key);
    };

    const scheduleAlternativePreload = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preloadAlternatives, { timeout: 1200 });
      } else {
        window.setTimeout(preloadAlternatives, 0);
      }
    };

    const matchedKey = () => {
      if (state.coverage === 'walls') return 's1';
      if (state.capacity === 'essential') return 'x1-60';
      return state.floorModel === 'x1a' ? 'x1a' : 'x1';
    };

    const updatePressedStates = () => {
      section.querySelectorAll('[data-matcher-group]').forEach((button) => {
        const group = button.dataset.matcherGroup;
        button.setAttribute('aria-pressed', String(state[group] === button.dataset.matcherValue));
      });
    };

    const updateQuestions = () => {
      const { coverage, capacity } = state;
      capacityQuestion.hidden = coverage !== 'floor';
      modelQuestion.hidden = coverage !== 'floor' || capacity !== 'extended';
      controls.dataset.questionCount = String(controls.querySelectorAll('[data-matcher-question]:not([hidden])').length);
    };

    const updateStageImage = (image) => {
      const signature = imageSignature(image);
      const request = ++stageRequest;

      if (!stage.firstElementChild) {
        displayedImageSignature = signature;
        stage.replaceChildren(image);
        return;
      }

      if (signature === displayedImageSignature) {
        stage.removeAttribute('aria-busy');
        return;
      }

      stage.setAttribute('aria-busy', 'true');
      image.loading = 'eager';
      image.fetchPriority = 'high';

      loadImage(image, 'high').then((loaded) => {
        if (request !== stageRequest) return;
        stage.removeAttribute('aria-busy');
        if (!loaded) return;

        displayedImageSignature = signature;
        stage.replaceChildren(image);
      });
    };

    const updateResult = () => {
      const key = matchedKey();
      const template = templates.get(key);
      if (!template) return;

      const fragment = template.content.cloneNode(true);
      const image = fragment.querySelector('img');
      const copy = fragment.querySelector('[data-matcher-copy]');
      updateStageImage(image);
      result.replaceChildren(copy);
    };

    const render = () => {
      updatePressedStates();
      updateQuestions();
      updateResult();
    };

    const anticipatedKey = (button) => {
      const group = button.dataset.matcherGroup;
      const value = button.dataset.matcherValue;
      if (group === 'coverage' && value === 'walls') return 's1';
      if (group === 'capacity' && value === 'essential') return 'x1-60';
      if (group === 'floor-model') return value;
      return matchedKey();
    };

    const preloadIntendedChoice = (event) => {
      const button = event.target.closest?.('[data-matcher-group]');
      if (!button || !section.contains(button)) return;
      preloadKey(anticipatedKey(button), event.type === 'pointerdown' ? 'high' : 'low');
    };

    section.addEventListener('pointerover', preloadIntendedChoice);
    section.addEventListener('pointerdown', preloadIntendedChoice);
    section.addEventListener('focusin', preloadIntendedChoice);

    section.addEventListener('click', (event) => {
      const button = event.target.closest('[data-matcher-group]');
      if (!button || !section.contains(button)) return;

      const group = button.dataset.matcherGroup;
      const value = button.dataset.matcherValue;

      if (group === 'coverage') {
        state.coverage = value;
        if (value === 'floor' && state.capacity !== 'essential') state.capacity = 'extended';
      } else if (group === 'capacity') {
        state.capacity = value;
      } else if (group === 'floor-model') {
        state.floorModel = value;
      }

      render();
    });

    initialized.add(section);
    render();

    if (canPreloadAhead() && 'IntersectionObserver' in window) {
      const preloadObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          preloadObserver.disconnect();
          scheduleAlternativePreload();
        },
        { rootMargin: '1200px 0px' },
      );
      preloadObserver.observe(section);
    }
  };

  const initializeAll = (root = document) => {
    if (root.matches?.(selector)) initialize(root);
    root.querySelectorAll?.(selector).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
